import React from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, FolderKanban, Egg, Leaf, Hammer, Flower2 } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { cn } from '../../lib/utils';

export const PROJECT_ICONS = {
  chicken_farming: Egg,
  school_garden: Leaf,
  masonry: Hammer,
  women_development: Flower2,
};

export const NavItem = ({ to, icon: Icon, label, collapsed, testId, badge }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all hover:bg-white/10',
        isActive && 'bg-white/10 border-l-4 border-secondary font-medium',
        collapsed && 'justify-center'
      )
    }
  >
    <Icon className="h-5 w-5 flex-shrink-0" />
    {!collapsed && (
      badge !== undefined ? (
        <span className="flex-1 flex items-center justify-between">
          {label}
          <span className="text-[10px] bg-secondary/30 text-white px-1.5 py-0.5 rounded-full">{badge}</span>
        </span>
      ) : (
        <span>{label}</span>
      )
    )}
  </NavLink>
);

export const SubNavItem = ({ to, icon: Icon, label, testId, indent = 'ml-4' }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      cn(
        `flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${indent} hover:bg-white/10 text-sm`,
        isActive && 'bg-white/15 text-secondary font-medium'
      )
    }
  >
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span className="truncate">{label}</span>
  </NavLink>
);

export const SidebarSection = ({ open, onOpenChange, icon: Icon, label, active, testId, children }) => (
  <Collapsible open={open} onOpenChange={onOpenChange}>
    <CollapsibleTrigger asChild>
      <button
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all hover:bg-white/10',
          active && 'bg-white/10 border-l-4 border-secondary font-medium'
        )}
        data-testid={testId}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
    </CollapsibleTrigger>
    <CollapsibleContent className="space-y-1 mt-1">{children}</CollapsibleContent>
  </Collapsible>
);

export const CollapsedLink = ({ to, active, icon: Icon }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      cn(
        'flex items-center justify-center px-3 py-2.5 rounded-lg transition-all hover:bg-white/10',
        (isActive || active) && 'bg-white/10 border-l-4 border-secondary font-medium'
      )
    }
  >
    <Icon className="h-5 w-5 flex-shrink-0" />
  </NavLink>
);

export const ProjectSubNavItem = ({ project, testId }) => {
  const Icon = PROJECT_ICONS[project.project_type] || FolderKanban;
  return <SubNavItem to={`/projects/${project.id}`} icon={Icon} label={project.name} testId={testId} />;
};
